import React, { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Badge,
  Button,
  Progress,
} from "reactstrap";
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Star,
  Clock,
  Award,
  Shield,
  Wrench,
  CheckCircle,
  FileText,
  Info,
  Image as ImageIcon,
  Calendar,
  Briefcase,
  Users,
  Link,
  ExternalLink,
} from "lucide-react";
import { Address } from "../../models/Address.interface";
import { Contractor } from "../../models/Contractor.interface";
import { Review } from "../../models/Review.interface";

interface ContractorProfileProps {
  contractor: Contractor;
}

const ContractorProfile: React.FC<ContractorProfileProps> = ({
  contractor,
}) => {
  // Mock data for the gallery - in a real app, this would come from the contractor object
  const [galleryImages] = useState([
    {
      id: "1",
      url: "https://via.placeholder.com/300x200?text=Project+1",
      caption: "Kitchen Remodel",
    },
    {
      id: "2",
      url: "https://via.placeholder.com/300x200?text=Project+2",
      caption: "Bathroom Renovation",
    },
    {
      id: "3",
      url: "https://via.placeholder.com/300x200?text=Project+3",
      caption: "Deck Construction",
    },
    {
      id: "4",
      url: "https://via.placeholder.com/300x200?text=Project+4",
      caption: "Living Room Makeover",
    },
    {
      id: "5",
      url: "https://via.placeholder.com/300x200?text=Project+5",
      caption: "Custom Cabinetry",
    },
    {
      id: "6",
      url: "https://via.placeholder.com/300x200?text=Project+6",
      caption: "Hardwood Flooring",
    },
  ]);

  // Updated reviews using the new Review interface
  const [reviews] = useState<Review[]>([
    {
      reviewer: "John D.",
      reviewerLocation: "Virginia Beach, VA",
      date: "2023-12-15",
      rating: 5,
      comment:
        "Excellent work! They completed our kitchen remodel on time and within budget. Very professional team.",
      project: "Kitchen Remodel",
      projectDate: "2023-11",
      projectCost: "$15,000-$20,000",
      images: [
        "https://via.placeholder.com/150?text=Kitchen+1",
        "https://via.placeholder.com/150?text=Kitchen+2",
      ],
    },
    {
      reviewer: "Sarah M.",
      reviewerLocation: "Norfolk, VA",
      date: "2023-10-20",
      rating: 4,
      comment:
        "Good quality work on our bathroom. There were some delays but they communicated well throughout the process.",
      project: "Bathroom Renovation",
      projectDate: "2023-09",
      projectCost: "$8,000-$10,000",
      response: {
        date: "2023-10-21",
        text: "Thank you for your feedback, Sarah! We appreciate your understanding regarding the delays with material availability. Were glad youre happy with the final result.",
      },
    },
    {
      reviewer: "Michael T.",
      reviewerLocation: "Chesapeake, VA",
      date: "2023-08-05",
      rating: 5,
      comment:
        "Fantastic deck build! The team was professional, clean, and finished ahead of schedule.",
      project: "Deck Construction",
      projectDate: "2023-07",
      projectCost: "$12,000-$15,000",
      images: ["https://via.placeholder.com/150?text=Deck+1"],
    },
  ]);

  const formatAddress = (address: Address | null | undefined) => {
    if (!address) return "Address not provided";
    return `${address.address1 || ""}, ${address.city || ""}, ${address.state || ""
      } ${address.zipCode || ""}`;
  };

  const renderStarRating = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<Star key={i} className="text-warning" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<Star key={i} className="text-warning opacity-50" />);
      } else {
        stars.push(<Star key={i} className="text-muted" />);
      }
    }

    return (
      <div className="d-flex align-items-center">
        <div className="me-2">{stars}</div>
        <span>{rating.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div className="contractor-profile-page py-4">
      <Container className="px-0">
        {/* Contractor Header */}
        <Card className="mb-4 shadow-sm">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col lg={3} className="text-center mb-4 mb-lg-0">
                {contractor.logo ? (
                  <img
                    src={contractor.logo}
                    alt={contractor.businessName}
                    className="rounded-circle object-fit-cover mb-3"
                    style={{ width: "120px", height: "120px" }}
                  />
                ) : (
                  <div
                    className="contractor-logo bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: "120px",
                      height: "120px",
                      fontSize: "48px",
                    }}
                  >
                    {contractor.businessName.charAt(0)}
                  </div>
                )}
                {contractor.rating && (
                  <div>
                    {renderStarRating(contractor.rating)}
                    <div className="text-muted mt-1">
                      {contractor.reviewCount || "0"} reviews
                    </div>
                  </div>
                )}
              </Col>
              <Col lg={6}>
                <h1 className="h2 mb-3">{contractor.businessName}</h1>
                {contractor.slogan && (
                  <p className="lead text-muted mb-3">{contractor.slogan}</p>
                )}
                <div className="mb-3">
                  {contractor.insurance?.active && (
                    <Badge color="success" className="me-2 mb-2">
                      Insured
                    </Badge>
                  )}
                  {contractor.licenses && contractor.licenses.length > 0 && (
                    <Badge color="primary" className="me-2 mb-2">
                      Licensed
                    </Badge>
                  )}
                  {contractor.bond?.active && (
                    <Badge color="info" className="me-2 mb-2">
                      Bonded
                    </Badge>
                  )}
                  {contractor.accreditation &&
                    contractor.accreditation.includes("BBB") && (
                      <Badge color="secondary" className="me-2 mb-2">
                        BBB Accredited
                      </Badge>
                    )}
                </div>
                <div className="contractor-details">
                  {contractor.address && (
                    <div className="mb-2 d-flex align-items-center">
                      <MapPin size={18} className="me-2 text-primary" />
                      <span>{formatAddress(contractor.address)}</span>
                    </div>
                  )}
                  {contractor.phone && (
                    <div className="mb-2 d-flex align-items-center">
                      <Phone size={18} className="me-2 text-primary" />
                      <a
                        href={`tel:${contractor.phone}`}
                        className="text-decoration-none"
                      >
                        {contractor.phone}
                      </a>
                    </div>
                  )}
                  {contractor.email && (
                    <div className="mb-2 d-flex align-items-center">
                      <Mail size={18} className="me-2 text-primary" />
                      <a
                        href={`mailto:${contractor.email}`}
                        className="text-decoration-none"
                      >
                        {contractor.email}
                      </a>
                    </div>
                  )}
                  {contractor.website && (
                    <div className="mb-2 d-flex align-items-center">
                      <Globe size={18} className="me-2 text-primary" />
                      <a
                        href={contractor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        {contractor.website}
                      </a>
                    </div>
                  )}
                </div>
              </Col>
              <Col lg={3} className="text-center text-lg-end">
                <div className="d-flex flex-column">
                  {contractor.specialOffer && (
                    <div className="special-offer mb-3 p-3 bg-light text-success rounded border border-success">
                      <strong>Special Offer:</strong>
                      <br />
                      {contractor.specialOffer}
                    </div>
                  )}
                  <Button color="primary" size="lg" className="mb-2 w-100">
                    Contact Now
                  </Button>
                  <Button color="success" size="lg" className="w-100">
                    Request a Quote
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Overview Section */}
        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <h3 className="mb-0">About {contractor.businessName}</h3>
          </CardHeader>
          <CardBody className="p-4">
            <Row>
              <Col lg={8}>
                <Row className="g-5">
                  <Col xs={12}>
                    <section>
                      <div className="about-section">
                        {contractor.about ||
                          contractor.description ||
                          "No detailed information provided by the contractor."}
                      </div>
                    </section>
                  </Col>
                  <Col xs={12}>
                    <section>
                      <h4 className="mb-4">Areas Served</h4>
                      <div className="areas-served">
                        {contractor.searches &&
                          contractor.searches.length > 0 ? (
                          <Row className="g-3">
                            {contractor.searches.map((search, index) => (
                              <Col md={4} key={index}>
                                <div className="p-3 bg-light rounded h-100">
                                  <div className="fw-bold">
                                    {search.city}, {search.state}
                                  </div>
                                  <small className="text-muted">
                                    {search.category}
                                  </small>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <p>No service areas specified.</p>
                        )}
                      </div>
                    </section>
                  </Col>
                  {contractor.hours && (
                    <Col xs={12}>
                      <section>
                        <h4 className="mb-4">Business Hours</h4>
                        <Card className="bg-light border-0 mb-0">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-3">
                              <Clock
                                size={20}
                                className="me-2 text-primary"
                              />
                              <h5 className="mb-0">Operating Hours</h5>
                            </div>
                            <div className="hours-list">
                              {Object.entries(contractor.hours).map(
                                ([day, hours]) => (
                                  <div
                                    key={day}
                                    className="d-flex justify-content-between py-2 border-bottom"
                                  >
                                    <span className="fw-bold">{day}</span>
                                    <span>{hours}</span>
                                  </div>
                                )
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </section>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col lg={4}>
                <Card className="mb-4 border-0 bg-light">
                  <CardHeader className="bg-primary text-white">
                    <h5 className="mb-0 text-white">Quick Info</h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="d-flex mb-4">
                      <Award size={24} className="me-3 text-primary" />
                      <div>
                        <h6 className="mb-1">Years in Business</h6>
                        <p className="mb-0">
                          {contractor.generalInfo?.match(
                            /(\d+)\s+years/i
                          )?.[1] || "Not specified"}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex mb-4">
                      <Shield size={24} className="me-3 text-primary" />
                      <div>
                        <h6 className="mb-1">Credentials</h6>
                        <p className="mb-0">
                          {[
                            contractor.licenses &&
                              contractor.licenses.length > 0
                              ? "Licensed"
                              : null,
                            contractor.insurance?.active ? "Insured" : null,
                            contractor.bond?.active ? "Bonded" : null,
                          ]
                            .filter(Boolean)
                            .join(", ") || "Not specified"}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex mb-4">
                      <Wrench size={24} className="me-3 text-primary" />
                      <div>
                        <h6 className="mb-1">Services</h6>
                        <p className="mb-0">
                          {contractor.categories?.slice(0, 3).join(", ")}
                          {contractor.categories &&
                            contractor.categories.length > 3
                            ? "..."
                            : ""}
                          {!contractor.categories?.length &&
                            "Not specified"}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex">
                      <CheckCircle
                        size={24}
                        className="me-3 text-primary"
                      />
                      <div>
                        <h6 className="mb-1">Projects Completed</h6>
                        <p className="mb-0">
                          {contractor.permitsCount ||
                            contractor.buildingPermits?.length ||
                            "Not specified"}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {contractor.reviews && contractor.reviews.length > 0 && (
                  <Card className="border-0">
                    <CardHeader className="bg-success text-white">
                      <h5 className="mb-0">Featured Review</h5>
                    </CardHeader>
                    <CardBody className="p-4">
                      <div className="featured-review">
                        {renderStarRating(reviews[0].rating)}
                        <p className="mt-3 mb-3">{reviews[0].comment}</p>
                        <div className="text-muted">
                          - {reviews[0].reviewer},{" "}
                          {reviews[0].reviewerLocation}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Services Section */}
        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <h3 className="mb-0">Services Offered</h3>
          </CardHeader>
          <CardBody className="p-4">
            <Row className="g-5">
              <Col md={12}>
                {contractor.categories &&
                  contractor.categories.length > 0 ? (
                  <Row>
                    {contractor.categories.map((category, index) => (
                      <Col lg={4} md={6} key={index} className="mb-3">
                        <div className="service-item p-4 bg-light rounded d-flex align-items-center h-100">
                          <div className="service-icon me-3 p-3 rounded bg-white">
                            <Wrench size={24} className="text-primary" />
                          </div>
                          <div className="service-name fw-bold">
                            {category}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <p>No specific services listed.</p>
                )}
              </Col>

              {(contractor.services || contractor.servicesProducts) && (
                <Col md={12}>
                  <div>
                    <h4 className="mb-4">Detailed Services</h4>
                    <Row>
                      <Col md={12}>
                        {contractor.services && (
                          <div className="mb-5">
                            <h5 className="mb-3">Services</h5>
                            <div className="list-group">
                              {contractor.services.map((service, index) => (
                                <div
                                  key={index}
                                  className="list-group-item list-group-item-action d-flex align-items-center"
                                >
                                  <CheckCircle
                                    size={18}
                                    className="me-3 text-success"
                                  />
                                  {service}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {contractor.servicesProducts && (
                          <div>
                            <h5 className="mb-3">Products</h5>
                            <div className="list-group">
                              {contractor.servicesProducts.map(
                                (product, index) => (
                                  <div
                                    key={index}
                                    className="list-group-item list-group-item-action d-flex align-items-center"
                                  >
                                    <Briefcase
                                      size={18}
                                      className="me-3 text-primary"
                                    />
                                    {product}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                </Col>
              )}

              {contractor.brands && contractor.brands.length > 0 && (
                <Col md={12}>
                  <div>
                    <h4 className="mb-4">Brands Used</h4>
                    <div className="brands-container">
                      {contractor.brands.map((brand, index) => (
                        <Badge
                          key={index}
                          color="light"
                          className="p-3 me-2 mb-2 fs-6"
                        >
                          {brand}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Gallery Section */}
        {/*
        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <h3 className="mb-0">Project Gallery</h3>
          </CardHeader>
          <CardBody className="p-4">
            <Row>
              <Col md={12}>
                {galleryImages.length > 0 ? (
                  <Row className="g-4">
                    {galleryImages.map((image) => (
                      <Col lg={4} md={6} key={image.id}>
                        <Card className="h-100 border-0 shadow-sm">
                          <div className="position-relative">
                            <img
                              src={image.url}
                              alt={image.caption}
                              className="card-img-responsive"
                              style={{
                                height: "200px",
                                objectFit: "cover",
                                width: "100%",
                              }}
                            />
                          </div>
                          <CardBody>
                            <h5 className="mb-0">{image.caption}</h5>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center p-5 bg-light rounded">
                    <ImageIcon size={48} className="text-muted mb-3" />
                    <h4>No Projects in Gallery</h4>
                    <p className="text-muted">
                      This contractor hasn't uploaded any project photos
                      yet.
                    </p>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
        */}

        {/* Reviews Section */}
        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Customer Reviews</h3>
              <Button color="primary">Write a Review</Button>
            </div>
          </CardHeader>
          <CardBody className="p-4">
            <Row className="g-4">
              <Col lg={8}>
                {reviews.length > 0 ? (
                  <div className="reviews-list">
                    <Row className="g-4">
                      {reviews.map((review, index) => (
                        <Col md={12} key={index}>
                          <Card className="mb-0 border-0 shadow-sm">
                            <CardBody className="p-4">
                              <div className="d-flex justify-content-between mb-3">
                                <div>
                                  <h5 className="mb-1">
                                    {review.reviewer}
                                  </h5>
                                  {review.reviewerLocation && (
                                    <div className="text-muted">
                                      {review.reviewerLocation}
                                    </div>
                                  )}
                                </div>
                                <div className="text-muted">
                                  {review.date &&
                                    new Date(
                                      review.date
                                    ).toLocaleDateString()}
                                </div>
                              </div>
                              <div className="mb-3">
                                {renderStarRating(review.rating)}
                              </div>
                              <p className="mb-4">{review.comment}</p>
                              {review.project && (
                                <div className="bg-light p-3 rounded mb-4">
                                  <div className="row">
                                    <div className="col-md-4 mb-2 mb-md-0">
                                      <strong>Project:</strong>
                                      <br />
                                      {review.project}
                                    </div>
                                    {review.projectDate && (
                                      <div className="col-md-4 mb-2 mb-md-0">
                                        <strong>Completed:</strong>
                                        <br />
                                        {review.projectDate}
                                      </div>
                                    )}
                                    {review.projectCost && (
                                      <div className="col-md-4">
                                        <strong>Approximate Cost:</strong>
                                        <br />
                                        {review.projectCost}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/*review.images &&
                                review.images.length > 0 && (
                                  <div className="review-images mb-4">
                                    <Row>
                                      {review.images.map((img, idx) => (
                                        <Col key={idx} xs={6} md={4} lg={3}>
                                          <img
                                            src={img}
                                            alt={`Review by ${review.reviewer}`}
                                            className="img-fluid rounded"
                                          />
                                        </Col>
                                      ))}
                                    </Row>
                                  </div>
                                )*/}
                              {review.response && (
                                <div className="contractor-response bg-light p-3 rounded mt-4">
                                  <h6 className="mb-2">
                                    Response from {contractor.businessName}
                                  </h6>
                                  <div className="text-muted small mb-2">
                                    {new Date(
                                      review.response.date
                                    ).toLocaleDateString()}
                                  </div>
                                  <p className="mb-0">
                                    {review.response.text}
                                  </p>
                                </div>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <div className="text-center p-5 bg-light rounded">
                    <Star size={48} className="text-muted mb-3" />
                    <h4>No Reviews Yet</h4>
                    <p className="text-muted">
                      Be the first to review this contractor.
                    </p>
                  </div>
                )}
              </Col>
              <Col lg={4}>
                <Row className="g-4">
                  <Col md={12}>
                    <Card className="mb-0 border-0 shadow-sm">
                      <CardHeader className="bg-primary text-white">
                        <h5 className="mb-0 text-white">
                          Rating Summary
                        </h5>
                      </CardHeader>
                      <CardBody className="p-4">
                        <div className="text-center mb-4">
                          <h2 className="display-4 mb-2">
                            {contractor.rating?.toFixed(1) || "N/A"}
                          </h2>
                          <div className="mb-2">
                            {renderStarRating(contractor.rating || 0)}
                          </div>
                          <p className="text-muted">
                            Based on {contractor.reviewCount || 0} reviews
                          </p>
                        </div>
                        <div className="rating-breakdown">
                          {[5, 4, 3, 2, 1].map((star) => {
                            const basePercentage = Math.max(
                              0,
                              Math.min(
                                100,
                                contractor.rating
                                  ? Math.round(
                                    100 -
                                    Math.abs(star - contractor.rating) *
                                    40
                                  )
                                  : 0
                              )
                            );
                            return (
                              <div
                                key={star}
                                className="d-flex align-items-center mb-2"
                              >
                                <div
                                  className="text-muted me-2"
                                  style={{ width: "60px" }}
                                >
                                  {star} stars
                                </div>
                                <Progress
                                  className="flex-grow-1"
                                  value={basePercentage}
                                  color={
                                    star >= 4
                                      ? "success"
                                      : star >= 3
                                        ? "info"
                                        : "warning"
                                  }
                                />
                                <div
                                  className="ms-2 text-muted"
                                  style={{ width: "40px" }}
                                >
                                  {basePercentage}%
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Card className="mb-0 border-0 shadow-sm">
                      <CardHeader className="bg-info text-white">
                        <h5 className="mb-0">Review Guidelines</h5>
                      </CardHeader>
                      <CardBody className="p-4">
                        <ul className="list-unstyled mb-0">
                          <li className="mb-2 d-flex align-items-center">
                            <CheckCircle
                              size={16}
                              className="text-success me-2"
                            />
                            Share your honest experience
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <CheckCircle
                              size={16}
                              className="text-success me-2"
                            />
                            Focus on the quality of work
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <CheckCircle
                              size={16}
                              className="text-success me-2"
                            />
                            Mention professionalism and timeliness
                          </li>
                          <li className="mb-2 d-flex align-items-center">
                            <CheckCircle
                              size={16}
                              className="text-success me-2"
                            />
                            Include photos if possible
                          </li>
                          <li className="d-flex align-items-center">
                            <CheckCircle
                              size={16}
                              className="text-success me-2"
                            />
                            Be respectful and constructive
                          </li>
                        </ul>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Credentials Section */}
        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <h3 className="mb-0">Professional Credentials</h3>
          </CardHeader>
          <CardBody className="p-4">
            <Row className="g-4">
              <Col lg={6}>
                <Card className="border-0 shadow-sm mb-4">
                  <CardHeader className="bg-white">
                    <h5 className="mb-0 d-flex align-items-center">
                      <Shield className="me-2 text-primary" />
                      License Information
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    {contractor.licenses &&
                      contractor.licenses.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>License Type</th>
                              <th>License Number</th>
                              <th>State</th>
                              <th>Expiration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contractor.licenses.map(
                              (license, index) => (
                                <tr key={index}>
                                  <td>{license.type || "Standard"}</td>
                                  <td>{license.number}</td>
                                  <td>{license.state}</td>
                                  <td>{license.expiration}</td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <Info size={32} className="text-muted mb-3" />
                        <p className="mb-0">
                          No license information provided
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
                {contractor.accreditation &&
                  contractor.accreditation.length > 0 && (
                    <Card className="border-0 shadow-sm mb-0">
                      <CardHeader className="bg-white">
                        <h5 className="mb-0 d-flex align-items-center">
                          <Award className="me-2 text-primary" />
                          Accreditations
                        </h5>
                      </CardHeader>
                      <CardBody className="p-4">
                        <div className="list-group list-group-flush">
                          {contractor.accreditation.map(
                            (accred, index) => (
                              <div
                                key={index}
                                className="list-group-item border-0 px-0 d-flex align-items-center"
                              >
                                <CheckCircle
                                  className="me-3 text-success"
                                  size={20}
                                />
                                <span className="fw-medium">
                                  {accred}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  )}
              </Col>
              <Col lg={6}>
                <Card className="border-0 shadow-sm mb-4">
                  <CardHeader className="bg-white">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FileText className="me-2 text-primary" />
                      Insurance Details
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    {contractor.insurance ? (
                      <div className="insurance-details">
                        <div className="mb-4">
                          <div className="d-flex align-items-center mb-2">
                            <Badge
                              color={
                                contractor.insurance.active
                                  ? "success"
                                  : "danger"
                              }
                              className="me-2 px-3 py-2"
                            >
                              {contractor.insurance.active
                                ? "Active"
                                : "Inactive"}
                            </Badge>
                            {contractor.insurance.verifiedDate && (
                              <span className="text-muted">
                                Verified on{" "}
                                {contractor.insurance.verifiedDate}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th scope="row" className="bg-light">
                                  Type
                                </th>
                                <td>
                                  {contractor.insurance.type ||
                                    "General Liability"}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  Provider
                                </th>
                                <td>
                                  {contractor.insurance.provider ||
                                    "Unknown"}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row" className="bg-light">
                                  Coverage Amount
                                </th>
                                <td>
                                  {contractor.insurance.coverageAmount
                                    ? `${contractor.insurance.coverageAmount}`
                                    : "Coverage amount not specified"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <Info size={32} className="text-muted mb-3" />
                        <p className="mb-0">
                          No insurance information provided
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
                {contractor.workersComp && (
                  <Card className="border-0 shadow-sm mb-0">
                    <CardHeader className="bg-white">
                      <h5 className="mb-0 d-flex align-items-center">
                        <Users className="me-2 text-primary" />
                        Workers' Compensation
                      </h5>
                    </CardHeader>
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-4">
                        <Badge
                          color={
                            contractor.workersComp.active
                              ? "success"
                              : "danger"
                          }
                          className="me-2 px-3 py-2"
                        >
                          {contractor.workersComp.active
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                        {contractor.workersComp.expirationDate && (
                          <span className="text-muted">
                            Expires:{" "}
                            {contractor.workersComp.expirationDate}
                          </span>
                        )}
                      </div>
                      <div className="bg-light p-3 rounded">
                        {contractor.workersComp.provider && (
                          <p className="mb-2">
                            <strong>Provider:</strong>{" "}
                            {contractor.workersComp.provider}
                          </p>
                        )}
                        {contractor.workersComp.policyNumber && (
                          <p className="mb-2">
                            <strong>Policy Number:</strong>{" "}
                            {contractor.workersComp.policyNumber}
                          </p>
                        )}
                        {contractor.workersComp.coverage && (
                          <p className="mb-0">
                            <strong>Coverage:</strong>{" "}
                            {contractor.workersComp.coverage}
                          </p>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>

            {/* Additional credential sections */}
            {contractor.bond && (
              <Row className="mt-4">
                <Col md={12}>
                  <Card className="mb-0 border-0 shadow-sm">
                    <CardHeader className="bg-white">
                      <h5 className="mb-0 d-flex align-items-center">
                        <Shield className="me-2 text-primary" />
                        Bonding Information
                      </h5>
                    </CardHeader>
                    <CardBody className="p-4">
                      {contractor.bond.active ? (
                        <div>
                          <div className="d-flex align-items-center mb-4">
                            <Badge
                              color="success"
                              className="me-2 px-3 py-2"
                            >
                              Bonded
                            </Badge>
                            {contractor.bond.verifiedDate && (
                              <span className="text-muted">
                                Verified on{" "}
                                {contractor.bond.verifiedDate}
                              </span>
                            )}
                          </div>
                          <Row>
                            <Col md={6}>
                              <div className="bg-light p-4 rounded h-100">
                                {contractor.bond.type && (
                                  <p className="mb-3">
                                    <strong>Bond Type:</strong>
                                    <br />
                                    {contractor.bond.type}
                                  </p>
                                )}
                                {contractor.bond.amount && (
                                  <p className="mb-0">
                                    <strong>Bond Amount:</strong>
                                    <br />${contractor.bond.amount}
                                  </p>
                                )}
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="bg-light p-4 rounded h-100">
                                {contractor.bond.issuer && (
                                  <p className="mb-3">
                                    <strong>Issuer:</strong>
                                    <br />
                                    {contractor.bond.issuer}
                                  </p>
                                )}
                                {contractor.bond.expirationDate && (
                                  <p className="mb-0">
                                    <strong>Expiration Date:</strong>
                                    <br />
                                    {contractor.bond.expirationDate}
                                  </p>
                                )}
                              </div>
                            </Col>
                          </Row>
                          {contractor.bond.description && (
                            <div className="mt-4">
                              <h6>Description</h6>
                              <p className="mb-0">
                                {contractor.bond.description}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center p-4">
                          <Info size={32} className="text-muted mb-3" />
                          <p className="mb-0">
                            This contractor is not bonded or bonding
                            information is not available.
                          </p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
            
            {contractor.employees && contractor.employees.length > 0 && (
              <Row className="mt-4">
                <Col md={12}>
                  <Card className="mb-0 border-0 shadow-sm">
                    <CardHeader className="bg-white">
                      <h5 className="mb-0 d-flex align-items-center">
                        <Users className="me-2 text-primary" />
                        Team Members
                      </h5>
                    </CardHeader>
                    <CardBody className="p-4">
                      <Row>
                        {contractor.employees.map((employee, index) => (
                          <Col
                            key={index}
                            lg={4}
                            md={6}
                            className="mb-4"
                          >
                            <Card className="employee-card border h-100 shadow-sm">
                              <CardBody className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                  <div className="employee-avatar me-3">
                                    {employee.avatar ? (
                                      <img
                                        src={employee.avatar}
                                        alt={employee.name}
                                        className="rounded-circle"
                                        width="64"
                                        height="64"
                                      />
                                    ) : (
                                      <div
                                        className="avatar-placeholder rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "64px",
                                          height: "64px",
                                          fontSize: "24px",
                                        }}
                                      >
                                        {employee.name?.charAt(0) ||
                                          "?"}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <h6 className="mb-1">
                                      {employee.name}
                                    </h6>
                                    <div className="text-muted">
                                      {employee.title || "Team Member"}
                                    </div>
                                  </div>
                                </div>
                                {employee.certifications && Array.isArray(employee.certifications) && employee.certifications.length > 0 && (
                                  <div className="mb-3">
                                    <div className="text-muted mb-2">
                                      Certifications:
                                    </div>
                                    <div>
                                      {employee.certifications.map(
                                        (cert: any, idx: any) => (
                                          <Badge
                                            key={idx}
                                            color="info"
                                            className="me-2 mb-2 px-3 py-2"
                                          >
                                            {cert}
                                          </Badge>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                                {employee.experience && (
                                  <div className="d-flex align-items-center text-muted">
                                    <Calendar
                                      size={16}
                                      className="me-2"
                                    />
                                    {employee.experience} years
                                    experience
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
            
            {contractor.buildingPermits &&
              contractor.buildingPermits.length > 0 && (
                <Row className="mt-4">
                  <Col md={12}>
                    <Card className="mb-0 border-0 shadow-sm">
                      <CardHeader className="bg-white">
                        <h5 className="mb-0 d-flex align-items-center">
                          <FileText className="me-2 text-primary" />
                          Building Permits & Projects
                        </h5>
                      </CardHeader>
                      <CardBody className="p-4">
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Permit #</th>
                                <th>Project Type</th>
                                <th>Address</th>
                                <th>Date</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contractor.buildingPermits.map(
                                (permit, index) => (
                                  <tr key={index}>
                                    <td>
                                      {permit.number ||
                                        `BP-${index + 1}`}
                                    </td>
                                    <td>
                                      {permit.type || "Construction"}
                                    </td>
                                    <td>
                                      {permit.address ||
                                        "Address not specified"}
                                    </td>
                                    <td>
                                      {permit.date ||
                                        "Date not specified"}
                                    </td>
                                    <td>
                                      <Badge
                                        color={
                                          permit.status === "Completed"
                                            ? "success"
                                            : permit.status ===
                                              "In Progress"
                                              ? "warning"
                                              : permit.status ===
                                                "Pending"
                                                ? "info"
                                                : "secondary"
                                        }
                                        className="px-3 py-2"
                                      >
                                        {permit.status || "Unknown"}
                                      </Badge>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
              
            {contractor.otherLinks &&
              contractor.otherLinks.length > 0 && (
                <Row className="mt-4">
                  <Col md={12}>
                    <Card className="mb-0 border-0 shadow-sm">
                      <CardHeader className="bg-white">
                        <h5 className="mb-0 d-flex align-items-center">
                          <Link className="me-2 text-primary" />
                          Additional Resources
                        </h5>
                      </CardHeader>
                      <CardBody className="p-4">
                        <Row>
                          {contractor.otherLinks.map((link, index) => (
                            <Col md={6} key={index} className="mb-3">
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-flex align-items-center text-decoration-none p-3 bg-light rounded"
                              >
                                <ExternalLink
                                  size={18}
                                  className="me-2 text-primary"
                                />
                                <span className="text-break">
                                  {
                                    link
                                      .replace(/^https?:\/\//, "")
                                      .split("/")[0]
                                  }
                                </span>
                              </a>
                            </Col>
                          ))}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
          </CardBody>
        </Card>

        {/* Call-to-Action Section */}
        <Card className="mt-4 mb-0 bg-light border-0 shadow-sm">
          <CardBody className="p-5">
            <Row className="align-items-center">
              <Col lg={8}>
                <h3 className="mb-2">
                  Ready to work with {contractor.businessName}?
                </h3>
                <p className="lead text-muted mb-lg-0">
                  Get in touch today to discuss your project needs and receive a
                  free consultation.
                </p>
              </Col>
              <Col lg={4} className="text-lg-end">
                <Button color="primary" size="lg" className="me-2 px-4">
                  Contact Now
                </Button>
                <Button color="success" size="lg" className="px-4">
                  Request Quote
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ContractorProfile;